import React, { useEffect, useState } from "react";
import {
    Box, Typography, TextField, Button, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Chip, Stack, TablePagination
} from "@mui/material";
import { Search, Add, Edit, Delete, Badge } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { URL } from "../config";

const API = `${URL}/employee-masters`;

// ── Role Mapper Helper ─────────────────────────────────────────────────────────
const roleMap = {
    1: { name: "Admin", color: "#7B1FA2", bg: "#F3E5F5" },
    2: { name: "Employee", color: "#1A2E8F", bg: "#E8EAF6" },
    3: { name: "Manager", color: "#F57C00", bg: "#FFF3E0" }
};

// ── Design Tokens ──────────────────────────────────────────────────────────────
const T = {
    ink: "#0D0D1A",
    navy: "#0F1B5F",
    navyLight: "#1A2E8F",
    accent: "#F97316",
    accentSoft: "#FFF0E5",
    surface: "#FFFFFF",
    mist: "#F5F6FA",
    border: "#E8EAF2",
    green: "#14875A",
    greenBg: "#E8F6EF",
    red: "#C0392B",
    redBg: "#FDECEA",
    silver: "#8892A4",
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');

  .emp-root { font-family: 'Sora', sans-serif; }

  .emp-card {
    background: ${T.surface};
    border-radius: 20px;
    border: 1px solid ${T.border};
    box-shadow: 0 4px 24px rgba(15,27,95,0.06);
    overflow: hidden;
  }

  .emp-add-btn {
    background: linear-gradient(135deg, ${T.navy} 0%, ${T.navyLight} 100%) !important;
    color: #fff !important;
    font-family: 'Sora', sans-serif !important;
    font-weight: 700 !important;
    font-size: 0.82rem !important;
    text-transform: none !important;
    border-radius: 12px !important;
    padding: 10px 22px !important;
    letter-spacing: 0.3px;
    box-shadow: 0 6px 18px rgba(15,27,95,0.25) !important;
    transition: all 0.22s ease !important;
    gap: 6px;
  }
  .emp-add-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 28px rgba(15,27,95,0.35) !important;
  }

  .emp-search input {
    font-family: 'Sora', sans-serif !important;
    font-size: 0.85rem !important;
    font-weight: 600 !important;
    color: ${T.ink} !important;
    padding: 10px 12px 10px 4px !important;
  }
  .emp-search fieldset { 
    border-color: ${T.border} !important; 
    border-radius: 12px !important; 
    transition: all 0.25s ease;
  }
  .emp-search:hover fieldset { 
    border-color: ${T.navyLight} !important; 
  }
  .emp-search.Mui-focused fieldset { 
    border-color: ${T.accent} !important; 
    border-width: 2px !important;
  }

  .emp-th {
    background: #F0F2F8 !important;
    color: #5A6380 !important;
    font-family: 'Sora', sans-serif !important;
    font-size: 0.72rem !important;
    font-weight: 700 !important;
    letter-spacing: 1.2px !important;
    text-transform: uppercase;
    padding: 16px 12px !important;
    border-bottom: 2px solid #E2E5F0 !important;
    white-space: nowrap;
  }

  .emp-tr {
    transition: background 0.15s ease;
  }
  .emp-tr:hover { background: ${T.mist} !important; }
  .emp-tr td { border-bottom: 1px solid ${T.border} !important; }
  .emp-tr:last-child td { border-bottom: none !important; }

  .emp-td {
    font-family: 'Sora', sans-serif !important;
    padding: 16px 12px !important;
    font-size: 0.85rem !important;
  }

  .emp-serial {
    font-family: 'DM Mono', monospace !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    color: ${T.silver} !important;
    letter-spacing: 0.5px;
  }

  .emp-name {
    font-family: 'Sora', sans-serif !important;
    font-size: 0.87rem !important;
    font-weight: 700 !important;
    color: ${T.ink} !important;
  }

  .emp-chip-active {
    background: ${T.greenBg} !important;
    color: ${T.green} !important;
    font-family: 'Sora', sans-serif !important;
    font-size: 0.7rem !important;
    font-weight: 700 !important;
    border-radius: 8px !important;
    height: 26px !important;
    letter-spacing: 0.3px;
  }
  .emp-chip-inactive {
    background: ${T.redBg} !important;
    color: ${T.red} !important;
    font-family: 'Sora', sans-serif !important;
    font-size: 0.7rem !important;
    font-weight: 700 !important;
    border-radius: 8px !important;
    height: 26px !important;
    letter-spacing: 0.3px;
  }

  .emp-edit-btn {
    background: ${T.mist} !important;
    color: ${T.navy} !important;
    border-radius: 9px !important;
    transition: all 0.18s ease !important;
  }
  .emp-edit-btn:hover {
    background: ${T.navy} !important;
    color: #fff !important;
    transform: scale(1.08);
  }

  .emp-del-btn {
    background: ${T.redBg} !important;
    color: ${T.red} !important;
    border-radius: 9px !important;
    transition: all 0.18s ease !important;
  }
  .emp-del-btn:hover {
    background: ${T.red} !important;
    color: #fff !important;
    transform: scale(1.08);
  }

  .emp-pagination .MuiTablePagination-toolbar {
    font-family: 'Sora', sans-serif !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    color: ${T.silver} !important;
  }
  .emp-pagination .MuiTablePagination-select,
  .emp-pagination .MuiTablePagination-displayedRows {
    font-family: 'Sora', sans-serif !important;
    font-weight: 600 !important;
    font-size: 0.8rem !important;
  }
  .emp-pagination .MuiIconButton-root {
    color: ${T.navy} !important;
    border-radius: 8px !important;
  }
  .emp-pagination .MuiIconButton-root:hover {
    background: ${T.navyLight}18 !important;
  }
`;

const StatPill = ({ label, value }) => (
    <Box sx={{
        display: "flex", flexDirection: "column", alignItems: "center",
        px: 2.5, py: 1.2,
        background: "rgba(249, 115, 22, 0.2) !important",
        borderRadius: "12px",
        border: `1px solid ${T.accent} !important`,
        minWidth: 80,
    }}>
        <Typography sx={{
            fontFamily: "'DM Mono', monospace", fontWeight: 800,
            fontSize: "1.3rem", color: "#FFFFFF", lineHeight: 1.1
        }}>{value}</Typography>
        <Typography sx={{
            fontFamily: "'Sora', sans-serif", fontWeight: 700,
            fontSize: "0.68rem", color: "rgba(255, 255, 255, 0.85)", textTransform: "uppercase",
            letterSpacing: "0.8px", mt: 0.3
        }}>{label}</Typography>
    </Box>
);

const EmployeeDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchEmployees();
    }, [location.state]);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const response = await fetch(API);
            const data = await response.json();
            setRows(Array.isArray(data) ? data : data.data || []);
        } catch (error) {
            console.error("Fetch Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${API}/${id}`, { method: "DELETE" });
            if (response.ok) {
                toast.success("Employee record deleted successfully", {
                    style: {
                        background: T.green,
                        color: "#fff",
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 700
                    }
                });
                fetchEmployees();
            } else {
                toast.error("Delete Failed!");
            }
        } catch (error) {
            toast.error("API Error!");
        }
    };

    const filtered = rows.filter(
        (r) => !search ||
            (r.employeeName || "").toLowerCase().includes(search.toLowerCase()) ||
            (r.mobileno || "").includes(search) ||
            (r.userName || "").toLowerCase().includes(search.toLowerCase())
    );

    const activeCount = rows.filter(r => r.isActive).length;
    const inactiveCount = rows.length - activeCount;

    return (
        <>
            <style>{styles}</style>
            <Box className="emp-root" sx={{
                width: "100%", minHeight: "100vh",
                bgcolor: T.mist,
                p: { xs: "16px", sm: "24px 28px" },
                boxSizing: "border-box",
                display: "flex", flexDirection: "column", gap: 2.5
            }}>
                <Toaster position="top-right" />

                {/* ── Page Header ── */}
                <Box sx={{
                    background: `linear-gradient(135deg, ${T.navy} 0%, #162472 100%)`,
                    borderRadius: "20px",
                    px: { xs: 2.5, sm: 4 }, py: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: `0 8px 32px rgba(15,27,95,0.22)`,
                    flexWrap: "wrap", gap: 2
                }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                            <Box sx={{
                                width: 38, height: 38, borderRadius: "10px",
                                background: "rgba(255,255,255,0.15)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                backdropFilter: "blur(8px)"
                            }}>
                                <Badge sx={{ color: T.accent, fontSize: 20 }} />
                            </Box>
                            <Typography sx={{
                                fontFamily: "'Sora',sans-serif", fontWeight: 800,
                                fontSize: { xs: "1rem", sm: "1.18rem" },
                                color: "#fff", letterSpacing: "0.5px"
                            }}>
                                Employee Master List
                            </Typography>
                        </Box>
                        <Typography sx={{
                            fontFamily: "'Sora',sans-serif", fontWeight: 500,
                            fontSize: "0.76rem", color: "rgba(255,255,255,0.5)",
                            ml: "50px"
                        }}>
                            Manage corporate organizational hierarchy and user access
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1.2} flexWrap="wrap">
                        <StatPill label="Total" value={rows.length} />
                        <StatPill label="Active" value={activeCount} />
                        <StatPill label="Inactive" value={inactiveCount} />
                    </Stack>
                </Box>

                {/* ── Toolbar ── */}
                <Stack direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    spacing={2}>

                    <Button
                        className="emp-add-btn"
                        variant="contained"
                        startIcon={<Add sx={{ fontSize: "1.1rem !important" }} />}
                        onClick={() => navigate("/employee-master")}
                    >
                        Add New Employee
                    </Button>

                    <Box sx={{ flexGrow: 1 }} />

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                        <Typography sx={{
                            fontFamily: "'Sora', sans-serif",
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: T.navy,
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                            height: "40px"
                        }}>
                            Search
                        </Typography>

                        <TextField
                            className="emp-search"
                            placeholder="Search by name, phone..."
                            size="small"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ m: 0, display: "flex", alignItems: "center" }}>
                                        <Search sx={{ color: T.navyLight, fontSize: 18, ml: 1 }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: "12px",
                                    bgcolor: T.surface,
                                    width: { xs: "100%", sm: "300px" },
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    boxShadow: "0 2px 12px rgba(15,27,95,0.04)",
                                    "&:hover": {
                                        boxShadow: "0 4px 16px rgba(15,27,95,0.08)",
                                    }
                                }
                            }}
                        />
                    </Stack>
                </Stack>

                {/* ── Table Card ── */}
                <Box className="emp-card" sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    <TableContainer sx={{ flexGrow: 1 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {/* ✅ Added 'ROLE' header in the checklist array */}
                                    {["SR.NO", "EMPLOYEE NAME", "CONTACT", "USERNAME", "PASSWORD", "DESIGNATION", "ROLE", "STATUS", "ACTION"].map((col) => (
                                        <TableCell key={col} className="emp-th" align="center">
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center"
                                            sx={{
                                                py: 10, fontFamily: "'Sora',sans-serif", fontWeight: 600,
                                                color: T.silver, fontSize: "0.85rem"
                                            }}>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                                                <Box sx={{
                                                    width: 36, height: 36, borderRadius: "50%",
                                                    border: `3px solid ${T.border}`,
                                                    borderTop: `3px solid ${T.navy}`,
                                                    animation: "spin 0.8s linear infinite",
                                                }} />
                                                <span>Fetching employee records…</span>
                                                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center"
                                            sx={{
                                                py: 10, fontFamily: "'Sora',sans-serif",
                                                fontWeight: 600, color: T.silver, fontSize: "0.85rem"
                                            }}>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                                                <Box sx={{
                                                    width: 48, height: 48, borderRadius: "14px",
                                                    background: T.mist, display: "flex",
                                                    alignItems: "center", justifyContent: "center", mb: 0.5
                                                }}>
                                                    <Search sx={{ color: T.silver, fontSize: 24 }} />
                                                </Box>
                                                No records found
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filtered
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => {
                                            // वर्तमान रोल आयडेंटिफाय करण्यासाठी मॅपरचा वापर केला
                                            const currentRole = roleMap[row.role] || { name: "User", color: T.silver, bg: T.mist };

                                            return (
                                                <TableRow key={row.employeeId} className="emp-tr">

                                                    {/* Serial */}
                                                    <TableCell className="emp-td emp-serial" align="center">
                                                        {String(page * rowsPerPage + index + 1).padStart(2, '0')}
                                                    </TableCell>

                                                    {/* Employee Name */}
                                                    <TableCell className="emp-td" align="center">
                                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.2, textAlign: "left" }}>
                                                            <Box sx={{ p: 0.6, borderRadius: "8px", bgcolor: T.accentSoft, color: T.accent, display: "flex" }}>
                                                                <Badge sx={{ fontSize: 16 }} />
                                                            </Box>
                                                            <Typography className="emp-name">{row.employeeName}</Typography>
                                                        </Box>
                                                    </TableCell>

                                                    {/* Contact */}
                                                    <TableCell className="emp-td" align="center" sx={{ fontWeight: 600, color: T.ink }}>
                                                        {row.mobileno || "—"}
                                                    </TableCell>

                                                    {/* Username */}
                                                    <TableCell className="emp-td" align="center" sx={{ fontWeight: 600, color: "#475569" }}>
                                                        {row.userName || "—"}
                                                    </TableCell>


                                                    {/* Password */}
                                                    <TableCell className="emp-td" align="center" sx={{ fontWeight: 600, color: T.ink }}>
                                                        {row.password || "—"}
                                                    </TableCell>

                                                    {/* Designation */}
                                                    <TableCell className="emp-td" align="center" sx={{ fontWeight: 600, color: T.ink }}>
                                                        {row.designation || "—"}
                                                    </TableCell>

                                                    {/* ✅ Added 'ROLE' badge element beautifully inside column layout */}
                                                    <TableCell className="emp-td" align="center">
                                                        <Chip
                                                            label={currentRole.name}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: currentRole.bg,
                                                                color: currentRole.color,
                                                                fontWeight: 700,
                                                                fontSize: "0.72rem",
                                                                borderRadius: "6px",
                                                                height: "24px"
                                                            }}
                                                        />
                                                    </TableCell>

                                                    {/* Status Chip */}
                                                    <TableCell className="emp-td" align="center">
                                                        <Chip
                                                            className={row.isActive ? "emp-chip-active" : "emp-chip-inactive"}
                                                            label={row.isActive ? "● Active" : "● Inactive"}
                                                        />
                                                    </TableCell>

                                                    {/* Actions */}
                                                    <TableCell className="emp-td" align="center">
                                                        <Stack direction="row" justifyContent="center" spacing={1}>
                                                            <IconButton
                                                                size="small"
                                                                className="emp-edit-btn"
                                                                onClick={() => navigate("/employee-master", { state: { editData: row } })}
                                                                title="Edit"
                                                            >
                                                                <Edit sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                className="emp-del-btn"
                                                                onClick={() => handleDelete(row.employeeId)}
                                                                title="Delete"
                                                            >
                                                                <Delete sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ borderTop: `1px solid ${T.border}` }} />

                    <TablePagination
                        className="emp-pagination"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filtered.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ color: T.silver, "& .MuiTablePagination-spacer": { flex: "none" } }}
                    />
                </Box>
            </Box>
        </>
    );
};

export default EmployeeDetails;